/**
 * Mémoire de navigation par onglet — demandé après usage.
 *
 * Comportement attendu, et il a **deux moitiés** qu'il ne faut pas confondre :
 *
 *   - toucher un onglet **depuis un autre onglet** ramène là où on l'avait
 *     laissé (le 14 août dans le journal, la section « Recettes » de la
 *     bibliothèque, le volet « Notifications » des réglages) ;
 *   - toucher l'onglet **où l'on se trouve déjà** ramène à sa page par défaut.
 *
 * C'est le comportement des barres d'onglets d'iOS, et il rend le second appui
 * utile au lieu d'être sans effet.
 *
 * ⚠️ **En mémoire, pas dans le stockage local.** Rouvrir l'application doit
 * l'ouvrir sur *aujourd'hui*, pas sur la journée qu'on consultait avant-hier :
 * la mémoire est celle d'une session de navigation, pas une préférence. C'est
 * l'inverse du choix de thème (`lib/theme.ts`), et pour la raison inverse.
 *
 * ⚠️ Ce module ne connaît **ni React ni le routeur** : il ne fait que retenir
 * une chaîne par onglet. C'est `App.tsx` qui l'alimente à chaque navigation et
 * `TabBar` qui l'interroge.
 */

/**
 * Racine d'un onglet, telle qu'elle apparaît dans l'URL.
 *
 * ⚠️ `musculation` est le 5ᵉ onglet de la V2 (étape 5). Le §2.5 avait annoncé
 * l'ajout comme « **deux lignes**, et le typecheck impose la seconde » : la
 * voici, et la seconde est bien le filtre de `tabOf()` ci-dessous — sans lui,
 * l'onglet s'éteindrait dès qu'on ouvre une routine.
 */
export type TabRoot = '' | 'bibliotheque' | 'progression' | 'reglages' | 'musculation';

/**
 * À quel onglet appartient un chemin.
 *
 * ⚠️ `/jour/:date` appartient au **journal**, dont la racine est `''` : c'est le
 * même écran à une autre date. Sans ce cas particulier, changer de jour
 * éteindrait l'onglet et le second appui n'aurait plus de sens.
 */
export function tabOf(pathname: string): TabRoot {
  const premier = pathname.split('/')[1] ?? '';
  if (premier === 'jour') return '';
  if (
    premier === 'bibliotheque' ||
    premier === 'progression' ||
    premier === 'reglages' ||
    premier === 'musculation'
  ) {
    return premier;
  }
  return '';
}

const derniers = new Map<TabRoot, string>();

/** Retient le chemin courant pour son onglet. Appelé à chaque navigation. */
export function remember(pathname: string): void {
  derniers.set(tabOf(pathname), pathname);
}

/**
 * Où mène un appui sur cet onglet.
 *
 * @param racine l'onglet visé
 * @param defaut sa page par défaut
 * @param actif vrai si on s'y trouve déjà — auquel cas on revient au défaut
 */
export function targetFor(racine: TabRoot, defaut: string, actif: boolean): string {
  if (actif) return defaut;
  return derniers.get(racine) ?? defaut;
}

/**
 * Oublie tout — utilisé à la déconnexion.
 *
 * Sans cela, la session suivante rouvrirait la bibliothèque sur la recette que
 * la précédente consultait. Ce n'est pas une fuite de données (rien ne sort du
 * navigateur), mais c'est un état qui n'a plus de propriétaire.
 */
export function forgetTabs(): void {
  derniers.clear();
}
